/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

// admin_console_ui/src/pages/LPOEntryModeSelector.jsx

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Box,
  Paper,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { useTenantSetting } from '../hooks/useSettings';
import { getAllLpoEntries, removeLpoEntry } from '../utils/offlineLpoStore'; // 🔁 SYNC HOOK
import { uploadLpo } from '../utils/uploadLpo'; // 🔁 SYNC HOOK
import toast from 'react-hot-toast'; // 🔁 SYNC HOOK
import { usePublicSettings } from "../hooks/usePublicSettings";


function isTokenValid(token) {
  try {
    const { exp } = jwtDecode(token);
    return exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

function getValidToken() {
  const lpoToken = localStorage.getItem("lpo_token");
  const userToken = localStorage.getItem("fluxonix_token");

  const lpoValid = lpoToken && isTokenValid(lpoToken);
  const userValid = userToken && isTokenValid(userToken);

  if (lpoValid) {
    return { token: lpoToken, userType: "lpo" };
  }

  if (userValid) {
    return { token: userToken, userType: "user" };
  }

  return null;
}

function LPOEntryModeSelector() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('manual');
  const { settings, isLoading } = usePublicSettings();
  const voiceOnlyMode = settings?.voice_only_mode === "true";

  // PATCH useEffect to allow either token
  useEffect(() => {
  const result = getValidToken();
  console.log("🟢 Token fetched is:", result);

  if (!result) {
    // Delay navigation by one tick to let console log flush
    setTimeout(() => {
      console.warn("🔁 Redirecting to OTP login...");
      navigate("/otp-login");
    }, 0);
  }
}, [navigate]);

   // 🔁 SYNC HOOK: Trigger offline LPO sync when page loads and user is online
  useEffect(() => {
    const token = getValidToken();
    if (!token || !isTokenValid(token)) return;

    const syncOfflineLpos = async () => {
      const entries = await getAllLpoEntries();
      if (!entries.length) return;

      console.log(`📦 Syncing ${entries.length} offline LPOs...`);
      for (const entry of entries) {
        try {
          await uploadLpo(entry);
          await removeLpoEntry(entry.id);
          toast.success(`✅ Synced LPO from ${entry.timestamp}`);
        } catch (err) {
          console.warn("❌ Failed to sync LPO:", err);
        }
      }
    };

    if (navigator.onLine) {
      syncOfflineLpos();
    }

    const handleOnline = () => {
      console.log("🌐 Online event fired → syncing LPOs...");
      syncOfflineLpos();
    };

    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, []); // no deps needed — only once per mount

  useEffect(() => {
    if (voiceOnlyMode) {
      setMode("voice"); // force voice mode if setting enabled
    }
  }, [voiceOnlyMode]);

  const handleContinue = () => {
    navigate(`/lpo-form/${mode}`);
  };

  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading submission options...
        </Typography>
      </Container>
    );
  }

  const isVoiceOnly = voiceOnlyMode;

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          How would you like to submit supplier info?
        </Typography>

        <RadioGroup
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          sx={{ mb: 3 }}
        >
          {!isVoiceOnly && (
            <FormControlLabel
              value="manual"
              control={<Radio />}
              label="Manual Form Entry + Photo Upload"
            />
          )}
          <FormControlLabel
            value="voice"
            control={<Radio />}
            label="Voice Note + Photo Upload"
          />
        </RadioGroup>

        <Box textAlign="right">
          <Button variant="contained" onClick={handleContinue}>
            Continue
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default LPOEntryModeSelector;
